import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import { Body, H1, H2, Label } from '../ui/Typography';
import { portfolioProjects } from '../../data/projects';

const featuredProjects = portfolioProjects.filter((project) => project.featured).slice(0, 4);

export default function HomePage() {
  return (
    <div className="portfolio-shell space-y-24 pb-24">
      <section className="grid gap-10 lg:grid-cols-[1.35fr_0.85fr] lg:items-end">
        <div className="space-y-8">
          <Label className="text-neonOrange">John Moore Design / Creative Direction</Label>
          <div className="space-y-6">
            <H1 className="max-w-5xl text-[clamp(3.5rem,10vw,8rem)] leading-[0.9]">
              Built for launch moments that need more than a logo.
            </H1>
            <Body className="max-w-2xl text-lg text-text-secondary md:text-xl">
              Identity systems, hospitality campaigns, and visual worlds shaped to feel immediate in the room and sharp on screen.
            </Body>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/projects">
              <Button className="px-6 py-3 text-sm font-mono uppercase tracking-[0.18em]">
                View selected work
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="ghost" className="px-6 py-3 text-sm font-mono uppercase tracking-[0.18em]">
                Start a conversation
              </Button>
            </Link>
          </div>
        </div>

        <div className="space-y-4 border border-border-subtle bg-surface-elevated/80 p-6 backdrop-blur-sm">
          <Label>What this studio does</Label>
          <ul className="grid gap-3 text-sm uppercase tracking-[0.12em] text-text-secondary md:grid-cols-2">
            <li>Identity systems</li>
            <li>Hospitality launches</li>
            <li>Campaign art direction</li>
            <li>Motion-ready social assets</li>
          </ul>
          <Body className="text-sm text-text-muted">
            The work is built to hold on menus, walls, social grids, teaser drops, and opening-night pressure.
          </Body>
        </div>
      </section>

      <section className="grid gap-6 border-y border-border-subtle py-8 text-sm uppercase tracking-[0.18em] text-text-secondary md:grid-cols-3">
        <div>
          <span className="block text-3xl font-display text-offWhite">10+</span>
          launch and campaign systems in active circulation
        </div>
        <div>
          <span className="block text-3xl font-display text-offWhite">Food + drink</span>
          hospitality, nightlife, and culture work with real-world constraints
        </div>
        <div>
          <span className="block text-3xl font-display text-offWhite">Digital first</span>
          built to feel sharp in feeds, decks, print, and environmental touchpoints
        </div>
      </section>

      <section className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="space-y-4">
          <Label>Selected work</Label>
          <H2 className="max-w-xl">A focused set of launch systems, hospitality identities, and campaign frames.</H2>
          <Body className="max-w-lg text-text-secondary">
            Each project is built as a flexible system, not a single hero image. The portfolio favors pieces that can survive real rollout conditions.
          </Body>
        </div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {featuredProjects.map((project) => (
            <article key={project.id} className="group border border-border-subtle bg-surface-card/80 p-4 transition-transform duration-300 hover:-translate-y-1">
              <div className="aspect-[4/5] overflow-hidden bg-charcoal">
                <img
                  src={project.images[0]}
                  alt={`${project.name} project preview`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                />
              </div>
              <div className="mt-4 space-y-2">
                <Label className="text-text-muted">{project.client}</Label>
                <h3 className="text-xl font-display uppercase tracking-tight">{project.name}</h3>
                <Body className="text-sm text-text-secondary">{project.description}</Body>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="grid gap-6 border border-border-subtle bg-black/30 p-8 lg:grid-cols-[1fr_auto] lg:items-center">
        <div className="space-y-3">
          <Label className="text-gold">Availability</Label>
          <H2 className="max-w-2xl">Currently taking on identity, campaign, and launch work for teams that need a sharper front door.</H2>
        </div>
        <Link to="/contact">
          <Button className="px-6 py-3 text-sm font-mono uppercase tracking-[0.18em]">
            Contact John Moore Design
          </Button>
        </Link>
      </section>
    </div>
  );
}
